// Internal floor-plan norms (Stage 9c). Validates the DETECTED rooms of a house
// floor against residential-design minimums (room area, corridor width). Pure JS,
// no DOM — unit-tested with Vitest. Runs inside House mode, separate from the
// site-level norms (norms.js) which check setbacks/clearances on the plot.
//
// These are advisory WARNINGS, not hard requirements — minimums vary by room
// count and regional codes; the panel shows the СП reference and the user can
// dismiss. Rule packs are JSON (rules/ru/floorplan-<version>.json).
//
// Violation shape:
//   { ruleId, severity, title, message, normRef, roomIndex, centroid,
//     valueM2?|widthM?, limitM2?|limitM? }

import floorplan2024_01 from "../rules/ru/floorplan-2024-01.json"

const PACKS = { "ru/2024-01": floorplan2024_01 }

export function loadFloorplanRuleSet(locale = "ru", version = "2024-01") {
  return PACKS[`${locale}/${version}`] ?? floorplan2024_01
}

// Minimal width of a polygon via the rotating-calipers idea: for each edge,
// project all vertices onto that edge's normal and take the extent; the minimum
// extent over all edges is the narrow dimension. EXACT for convex polygons —
// which covers the real case here, since a corridor is a single rectangle from
// the room tool. For a CONCAVE room (e.g. an L-shaped corridor) the extent in
// each axis spans the whole bounding box, so this over-estimates the true
// passage width — a conservative UNDER-warning, never a false alarm. True
// concave passage width would need a medial-axis analysis; out of scope here.
export function roomMinWidthM(polygon) {
  const n = polygon?.length ?? 0
  if (n < 3) return 0
  let min = Infinity
  for (let i = 0; i < n; i++) {
    const [ax, ay] = polygon[i]
    const [bx, by] = polygon[(i + 1) % n]
    const ex = bx - ax
    const ey = by - ay
    const len = Math.hypot(ex, ey)
    if (len < 1e-9) continue
    const nx = -ey / len
    const ny = ex / len
    let lo = Infinity
    let hi = -Infinity
    for (const [px, py] of polygon) {
      const d = (px - ax) * nx + (py - ay) * ny
      if (d < lo) lo = d
      if (d > hi) hi = d
    }
    const w = hi - lo
    if (w < min) min = w
  }
  return min === Infinity ? 0 : min
}

// Validate a floor's rooms against the rule set. `rooms` are the persisted-shape
// rooms ({ type, areaM2, polygon, centroid }). disabledRuleIds is a Set<string>.
// Returns an array of violations (see shape above).
export function validateFloor({ rooms, ruleSet, disabledRuleIds } = {}) {
  const out = []
  if (!ruleSet?.rules || !Array.isArray(rooms) || rooms.length === 0) return out
  const disabled = disabledRuleIds ?? new Set()
  rooms.forEach((room, roomIndex) => {
    for (const rule of ruleSet.rules) {
      if (disabled.has(rule.id)) continue
      if (!rule.roomTypes?.includes(room.type)) continue
      if (rule.kind === "min-room-area") {
        if (room.areaM2 < rule.minAreaM2) {
          out.push(base(rule, room, roomIndex, {
            valueM2: room.areaM2,
            limitM2: rule.minAreaM2,
          }))
        }
      } else if (rule.kind === "min-room-width") {
        const widthM = roomMinWidthM(room.polygon)
        if (widthM < rule.minWidthM) {
          out.push(base(rule, room, roomIndex, {
            widthM,
            limitM: rule.minWidthM,
          }))
        }
      }
    }
  })
  return out
}

function base(rule, room, roomIndex, extra) {
  return {
    ruleId: rule.id,
    severity: rule.severity ?? "warning",
    title: rule.title,
    message: rule.message,
    normRef: rule.normRef,
    roomIndex,
    centroid: room.centroid,
    ...extra,
  }
}

// Group violations by roomIndex for per-room panel badges.
export function violationsByRoomIndex(violations) {
  const map = new Map()
  for (const v of violations) {
    if (!map.has(v.roomIndex)) map.set(v.roomIndex, [])
    map.get(v.roomIndex).push(v)
  }
  return map
}
